import React, { useState, useEffect, useCallback } from 'react';
import {
  Wand2,
  Plus,
  Search,
  MoreVertical,
  Copy,
  Trash,
  Power,
  Pencil,
  Loader2,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Sidebar } from '@/components/common/Sidebar';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/auth';
import { AttendanceMaskEditor } from '@/components/ehr/AttendanceMaskEditor';
import {
  listAttendanceTemplates,
  deleteAttendanceTemplate,
  duplicateAttendanceTemplate,
  updateAttendanceTemplate,
  type AttendanceTemplateDTO,
} from '@/services/attendanceTemplates';

export default function MascarasPage() {
  const { user, logout } = useAuth();
  const [templates, setTemplates] = useState<AttendanceTemplateDTO[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<AttendanceTemplateDTO[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Modal editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<AttendanceTemplateDTO | undefined>();

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listAttendanceTemplates(true); // includeInactive = true
      if (res.success && res.data) {
        setTemplates(res.data);
      } else {
        toast.error(res.error || 'Erro ao carregar máscaras');
      }
    } catch {
      toast.error('Falha ao carregar máscaras');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    const term = search.toLowerCase();
    setFilteredTemplates(
      templates.filter(
        (t) =>
          t.name.toLowerCase().includes(term) ||
          (t.category && t.category.toLowerCase().includes(term)),
      ),
    );
  }, [search, templates]);

  const handleCreate = () => {
    setTemplateToEdit(undefined);
    setIsEditorOpen(true);
  };

  const handleEdit = (template: AttendanceTemplateDTO) => {
    setTemplateToEdit(template);
    setIsEditorOpen(true);
  };

  const handleDuplicate = async (id: string) => {
    try {
      const res = await duplicateAttendanceTemplate(id);
      if (res.success) {
        toast.success('Máscara duplicada com sucesso!');
        loadTemplates();
      } else {
        toast.error(res.error || 'Erro ao duplicar máscara');
      }
    } catch {
      toast.error('Falha ao duplicar máscara');
    }
  };

  const handleToggleActive = async (template: AttendanceTemplateDTO) => {
    try {
      const res = await updateAttendanceTemplate(template.id, { isActive: !template.isActive });
      if (res.success) {
        toast.success(`Máscara ${template.isActive ? 'desativada' : 'ativada'}!`);
        loadTemplates();
      } else {
        toast.error(res.error || 'Erro ao alterar status da máscara');
      }
    } catch {
      toast.error('Falha ao alterar status');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente remover esta máscara? Ela será desativada para manter histórico.')) return;
    try {
      const res = await deleteAttendanceTemplate(id);
      if (res.success) {
        toast.success('Máscara removida com sucesso!');
        loadTemplates();
      } else {
        toast.error(res.error || 'Erro ao remover máscara');
      }
    } catch {
      toast.error('Falha ao remover máscara');
    }
  };

  const getFilledBlocksCount = (content: any) => {
    return Object.keys(content).filter((key) => {
      const val = content[key];
      if (typeof val === 'string') return val.trim().length > 0;
      if (val && typeof val === 'object') {
        if ('predefined' in val) return val.predefined.length > 0 || !!val.freeText;
        return Object.keys(val).length > 0;
      }
      return false;
    }).length;
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-neutral-900">
      <Sidebar userRole={user?.role as UserRole} userName={user?.name || ''} onLogout={logout} />
      <div className="flex-1 overflow-auto">
        <main className="p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Wand2 className="w-6 h-6 text-teal-600" />
                Máscaras de Atendimento
              </h1>
              <p className="text-gray-500 mt-1">
                Gerencie seus templates estruturados para agilizar as evoluções clínicas.
              </p>
            </div>
            <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700 text-white gap-2">
              <Plus className="w-4 h-4" />
              Nova Máscara
            </Button>
          </div>

          {/* Controls */}
          <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-gray-100 dark:border-neutral-700 mb-6 shadow-sm">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nome ou especialidade..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* List */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-teal-600">
              <Loader2 className="w-10 h-10 animate-spin mb-4" />
              <p>Carregando máscaras...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-neutral-800 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-700">
              <Wand2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                Nenhuma máscara encontrada
              </h3>
              <p className="text-gray-500 mb-6">
                {search
                  ? 'Tente um termo de busca diferente.'
                  : 'Você ainda não possui máscaras de atendimento cadastradas.'}
              </p>
              {!search && (
                <Button onClick={handleCreate} variant="outline" className="border-teal-200 text-teal-700">
                  Criar minha primeira máscara
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className={`bg-white dark:bg-neutral-800 rounded-2xl border ${
                    template.isActive
                      ? 'border-gray-200 dark:border-neutral-700'
                      : 'border-gray-200 dark:border-neutral-700 opacity-60'
                  } p-5 hover:shadow-md transition-shadow relative flex flex-col`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        {template.isFeatured && <span className="text-amber-500 text-sm">★</span>}
                        <h3 className="font-bold text-gray-900 dark:text-white truncate">
                          {template.name}
                        </h3>
                      </div>
                      {template.category && (
                        <Badge variant="secondary" className="text-xs bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                          {template.category}
                        </Badge>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="-mr-2 -mt-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-neutral-700 focus:outline-none">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(template)}>
                          <Pencil className="w-4 h-4 mr-2" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(template.id)}>
                          <Copy className="w-4 h-4 mr-2" /> Duplicar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(template)}>
                          <Power className="w-4 h-4 mr-2" /> {template.isActive ? 'Desativar' : 'Ativar'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 focus:bg-red-50"
                        >
                          <Trash className="w-4 h-4 mr-2" /> Remover
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-auto space-y-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Check className="w-4 h-4 text-teal-500" />
                      <span>{getFilledBlocksCount(template.content)} seções preenchidas</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-400 pt-4 border-t border-gray-100 dark:border-neutral-700">
                      <span>Usos: {template.usageCount}</span>
                      <span>Atualizado em: {new Date(template.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {isEditorOpen && (
        <AttendanceMaskEditor
          template={templateToEdit}
          onClose={() => setIsEditorOpen(false)}
          onSaved={() => {
            setIsEditorOpen(false);
            loadTemplates();
          }}
        />
      )}
    </div>
  );
}
